import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { X, Users, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ContactEmailTypeahead — Search CRM contacts by name/email with tag-style multi-select.
 * Props: value (array of {email, name?}), onChange, placeholder, allowFreeText (type any email)
 */
export default function ContactEmailTypeahead({ value = [], onChange, placeholder = 'Search contacts...', allowFreeText = true }) {
    const { getAuthHeader } = useAuth();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (query.length < 2) { setResults([]); return; }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/contacts?search=${encodeURIComponent(query)}&page_size=8`, { headers: getAuthHeader() });
                const contacts = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setResults(contacts.filter(c => c.email && !value.find(v => v.email === c.email)));
                setShowDropdown(true);
            } catch {}
            finally { setLoading(false); }
        }, 300);
    }, [query, getAuthHeader, value]);

    const addContact = (contact) => {
        onChange([...value, { email: contact.email, name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(), contact_id: contact.contact_id }]);
        setQuery('');
        setResults([]);
        setShowDropdown(false);
        inputRef.current?.focus();
    };

    const addFreeEmail = () => {
        const email = query.trim();
        if (email && email.includes('@') && !value.find(v => v.email === email)) {
            onChange([...value, { email, name: '' }]);
            setQuery('');
        }
    };

    const remove = (email) => {
        onChange(value.filter(v => v.email !== email));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (results.length > 0) { addContact(results[0]); }
            else if (allowFreeText && query.includes('@')) { addFreeEmail(); }
        }
        if (e.key === 'Backspace' && !query && value.length > 0) {
            remove(value[value.length - 1].email);
        }
    };

    return (
        <div className="relative">
            <div className="flex flex-wrap gap-1 p-1.5 border border-stone-200 rounded-lg bg-white min-h-[38px] focus-within:ring-2 focus-within:ring-stone-900/20">
                {value.map(v => (
                    <span key={v.email} className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-xs px-2 py-0.5 rounded-full">
                        {v.name || v.email}
                        <button onClick={() => remove(v.email)} className="text-stone-400 hover:text-red-500">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown} onFocus={() => query.length >= 2 && setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    placeholder={value.length === 0 ? placeholder : ''}
                    className="flex-1 min-w-[120px] text-sm border-none outline-none bg-transparent px-1 py-0.5" />
            </div>

            {showDropdown && results.length > 0 && (
                <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {results.map(c => (
                        <button key={c.contact_id} onMouseDown={() => addContact(c)}
                            className="w-full px-3 py-2 text-left hover:bg-stone-50 flex items-center gap-2">
                            <Users className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                            <div className="min-w-0">
                                <div className="text-xs font-medium text-stone-700 truncate">
                                    {c.first_name} {c.last_name}
                                </div>
                                <div className="text-[10px] text-stone-400 truncate">{c.email}</div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
